/**
 * @module app.pp
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { WizardNavigator, WizardNavigatorFinalSubmitEvent } from 'app/core/components/wizard/wizard-navigator';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { AuthContext } from 'app/um/lib/auth-context';
import { AuthService } from 'app/um/services/auth-service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { IModalData, openGlobalModal, closeGlobalModal, IDialogOptions } from 'app/shared/elements/ess-global-modal';
import { apiCall } from 'app/shared/lib/essette-api';
import { DocumentsService } from 'app/core/services/documents-service';
import { NoteService } from 'app/core/services/note-service';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { IDocumentsConfig, INotesConfig } from 'app/core/models/essette-interfaces';
import { MyAuthorizationsReloadEvent } from 'app/pp/lib/events';
import * as UM from 'app/um/models/essette-api';

interface IAddServiceModalData {
    auth: UM.Auth;
}


@autoinject()
export class AddServicePage extends ComponentBase {

    ////****************** Element References ***************************************////

    @bindable
    protected auth: UM.Auth = {};

    protected $collapsible!: EssAccordionCustomAttribute;
    protected $steps: IWizardStep[] = [];
    protected $wizard!: WizardNavigator;
    protected accordionElement!: HTMLUListElement;
    public serviceOption: string = 'service';
    protected missingFields: string[] = [];

    protected hasPageAccess: boolean = false;

    protected loaded: Promise<void>;
    protected load!: () => void;

    protected listItemIndex: number = 0;
    protected latestStepIndex: number = 0;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        addTargetProperty: (note: UM.AuthNote) => { note.auth = { id: this.auth?.id } },
        excludeSystem: false
    }

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        categoryFlagCodes: ['PP.View'],
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth?.id } }
    };

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly context: AuthContext,
        private readonly authService: AuthService,
        private readonly events: EventAggregator,
        private readonly documentService: DocumentsService,
        private readonly noteService: NoteService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;

        this.loaded = new Promise<void>((resolve) => this.load = resolve);
    }

    protected async activate(modelData: IAddServiceModalData): Promise<void> {
        if(!modelData.auth.id) return;
        this.context.auth = { id: modelData.auth.id, services: [], documents: [], notes: [] };
        this.context.apiSettings = await this.context.loadSettings(modelData.auth.id);
        this.context.initializeSettings();
        this.auth = this.context.auth;
    }

    protected attached(): void {
        this.load();
        this.loaded.then(() => this.$collapsible.open(0));
    }

    protected async handleFinalSubmit(event: WizardNavigatorFinalSubmitEvent) {
        const auth: UM.Auth = event.detail.item;
        this.submitServiceRequest(auth);
    }

    ////****************** Functions ************************************************////

    protected incrementStepCounter(): number {
        return ++this.listItemIndex;
    }

    protected isSectionEnabled(item: Element): boolean {
        return this.$wizard?.isSectionEnabled(item);
    }

    protected onCloseSection(stepIndex: number): void {
        if(stepIndex !== this.latestStepIndex) this.$wizard.closeStep(stepIndex);

        this.accordionElement.children[stepIndex]?.querySelector('div:first-child')?.setAttribute("aria-expanded", "false");
    }

    protected onOpenSection(stepIndex: number): void {
        this.accordionElement.children[stepIndex]?.querySelector('div:first-child')?.setAttribute("aria-expanded", "true");
        this.$wizard.currentStep = this.$wizard.steps[stepIndex];
    }

    protected async submitServiceRequest(auth: UM.Auth): Promise<void> {
        if(!auth) { return; }

        try {
            this.state.beginSave();

            const [serviceSuccess, documentSuccess, noteSuccess] = await Promise.all(
                [
                    this.saveService(auth),
                    this.saveDocument(auth),
                    this.saveNote(auth)
                ]
            );

            this.toaster.standard.saved('um:service.$', serviceSuccess && documentSuccess && noteSuccess);

            if(this.context.serviceRequestSetAuthStatus) await this.context.setAuthStatus(auth.id, 'PP.Auth.Service.Added');
            if(this.context.serviceRequestRouteToQueue) this.context.triggerQueueRoutingRules(auth.id, 'PP.Auth.Service.Added');
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
            closeAddServiceModal();
            this.events.publish(new MyAuthorizationsReloadEvent());
        }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async saveService(auth: UM.Auth): Promise<boolean> {
        if(!auth.id || auth.services?.length != 1) return false;

        const service: UM.AuthService = auth.services![0];

        const saveService: UM.AuthService = {
            auth: {
                id: auth.id
            },
            definition: {
                id: service.definition?.id
            },
            quantity: {
                requested: service.quantity?.requested
            },
            charge: {
                requested: service.charge?.requested
            },
            authorizedDate: service.authorizedDate,
            expirationDate: service.expirationDate,
            bodySite: { id: service.bodySite?.id },
            bodySubSite: { id: service.bodySubSite?.id }
        };

        if(service.modifiers != null && service.modifiers.length > 0) saveService.services![0].modifiers = [] as UM.AuthModifierDetailModel[];
        service.modifiers?.forEach(m => {
            saveService.services![0].modifiers?.push({ id: m.id });
        });

        try {
            const response = await this.authService.saveAuthService(saveService);
            if(response.isSuccessful) return true;

            this.logger.error(response.resultText!);
            return false;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async saveDocument(auth: UM.Auth): Promise<boolean> {
        if(auth.documents?.length != 1) return true;

        const document: UM.AuthDocument = auth.documents![0];
        if(!document || !document.category) return false;

        this.documentService.configure(this.documentsConfig);

        const saveDocument: UM.AuthDocument = {
            auth: { id: auth.id },
            category: { id: document.category.id },
            description: document.description,
            file: { name: document.file?.name },
            summary: document.summary,
            upload: { token: { value: document.upload?.token?.value } }
        };

        try {
            const response = await this.documentService.attachDocument(saveDocument);

            if(response.isSuccessful) {
                return true;
            }
            else {
                this.logger.error(response.resultText!);
                return false;
            }
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async saveNote(auth: UM.Auth): Promise<boolean> {
        if(auth.notes?.length != 1 || !auth.notes[0].text || auth.notes[0].text.length == 0) return true;

        const note: UM.AuthNote = auth.notes![0];
        if(!note || !note.category) return false;

        this.noteService.configure(this.notesConfig);

        const saveNote: UM.AuthNote = {
            auth: { id: auth.id },
            text: note.text,
            category: getReference(note.category),
            flags: getReferences(note.category.flags)
        }

        try {
            const response = await this.noteService.saveNote(saveNote);

            if(response.isSuccessful) {
                return true;
            }
            else {
                this.logger.error(response.resultText!);
                return false;
            }
        }
        catch(error) {
            throw error;
        }
    }

}

export function closeAddServiceModal(): void {
    closeGlobalModal();
}

/**
* Opens the add service modal with the global dialog
*/
export function openAddServiceModal(auth: UM.Auth): void {

    const dialogOptions: IDialogOptions = {
        width: '10',
        height: '10',
        helpTopic: 'pp.auth.add-service',
        title: {
            key: 'pp:local.addService.$'
        },
        print: false,
        confirmClose: true,
        confirmType: 'cancelChanges',
        confirmMessage: 'um:service.$'
    };

    const addServiceModalData: IAddServiceModalData = {
        auth: auth
    };

    const modalData: IModalData = {
        componentId: 'app/pp/components/modify/add-service',
        modelData: addServiceModalData,
        dialogOptions: dialogOptions
    };

    openGlobalModal(modalData);
}
